/**
 * Display helpers for Rates and pools (ALN-33, docs/pages/rates.md A). Rates are numeric cents with
 * up to four decimals (ADR 0002); the page shows and takes them in dollars. Nothing here does money
 * math: conversions shift the decimal point in the string, so no float ever reaches a saved value.
 */

/** A percentage to a tenth, rounded to nearest: 66.41 → "66.4%" (F10). */
export function pctTenth(pct: number): string {
  return `${(Math.round(pct * 10) / 10).toFixed(1)}%`;
}

/** A rate in cents shown in dollars: under $10 to three decimals at most (0.085, 0.29, 1.00), otherwise two (45.00). */
export function rateText(cents: number): string {
  const dollars = cents / 100;
  return dollars < 10
    ? dollars.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 3, useGrouping: false })
    : dollars.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: false });
}

/**
 * A typed dollar amount as a cents string for the API ("45" → "4500", "0.085" → "8.5"), or null when
 * it isn't a number of zero or more. Commas and a leading $ are allowed; up to six dollar decimals
 * (four of a cent).
 */
export function dollarsToCents(typed: string): string | null {
  const s = typed.trim().replace(/^\$/, "").replace(/,/g, "");
  const m = /^(\d*)(?:\.(\d{0,6}))?$/.exec(s);
  if (!m || (!m[1] && !m[2])) return null;
  const frac = (m[2] ?? "").padEnd(2, "0");
  const whole = `${m[1] ?? ""}${frac.slice(0, 2)}`.replace(/^0+(?=\d)/, "") || "0";
  const rest = frac.slice(2).replace(/0+$/, "");
  return rest ? `${whole}.${rest}` : whole;
}

/** Whole dollars for a cents amount (idle, plan total): $497. */
export function wholeDollars(cents: number): string {
  return (cents / 100).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0, minimumFractionDigits: 0 });
}

/** Dollars and cents: $94.71. */
export function usd(cents: number): string {
  return (cents / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });
}

/** A quantity such as this month's activity: 11,345 or 79.7. */
export function qty(n: number): string {
  return n.toLocaleString("en-US", { maximumFractionDigits: 3 });
}

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
/** "2026-08" → "August 2026". */
export function monthName(key: string): string {
  const [y, m] = key.split("-").map(Number);
  return `${MONTHS[(m ?? 1) - 1]} ${y}`;
}
